import React, { ReactNode } from 'react';
import { Modifier } from 'react-day-picker/types/Modifiers';
import DayPickerInputValue from '../edit/DayPickerInputValue';

/**
 * A labelled date question in the booking flow.
 *
 * The three date inputs on CollectionDelivery were previously a bare
 * `<DayPickerInput>` under a `<section>Collection Date:</section>` — a
 * sectioning element used as a caption, with no `<label for>` anywhere, so
 * the control had no accessible name at all. They also gave no hint as to
 * which days could be picked: weekends, bank holidays and the lead time are
 * all disabled in the calendar, but the customer only discovered a rule by
 * choosing a date and being told off in red.
 *
 * So: a real label, the constraint stated up front as a hint, and the error
 * rendered once — here, wired to the input through `aria-describedby` —
 * rather than twice in two different styles.
 */
interface DateFieldProps {
  id: string;
  label: string;
  value?: Date;
  onChange: (day: Date | undefined) => void;
  disabledDays: Modifier | Modifier[];
  /** The rule, stated before the customer can break it. */
  hint?: ReactNode;
  /** Message shown once the field has been attempted and is wrong. */
  error?: string | null;
  optional?: boolean;
  onValidationError?: (error: string | null) => void;
}

function CalendarGlyph(): JSX.Element {
  return (
    <svg
      className="date-field__icon"
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <rect x="2.6" y="4.2" width="14.8" height="13.2" rx="2.4" />
      <path d="M2.6 8.4h14.8" />
      <path d="M6.8 2.6v3.2M13.2 2.6v3.2" />
    </svg>
  );
}

export default function DateField({
  id,
  label,
  value,
  onChange,
  disabledDays,
  hint,
  error,
  optional,
  onValidationError,
}: DateFieldProps): JSX.Element {
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;
  const describedBy =
    [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
    undefined;

  return (
    <div className={`date-field${error ? ' date-field--invalid' : ''}`}>
      <label className="date-field__label" htmlFor={id}>
        {label}
        {optional && (
          <span className="date-field__optional"> (optional)</span>
        )}
      </label>

      <div className="date-field__control">
        <CalendarGlyph />
        <DayPickerInputValue
          id={id}
          value={value ? value.getTime() : undefined}
          handleStateChange={(day) => onChange(day as Date | undefined)}
          disabled={false}
          disabledDays={disabledDays}
          onValidationError={onValidationError}
          ariaDescribedBy={describedBy}
          ariaInvalid={!!error}
          // The page owns the message: it has to say the same thing in the
          // summary panel, and two copies in two styles is worse than one.
          renderError={false}
        />
      </div>

      {hint && (
        <p className="date-field__hint" id={hintId}>
          {hint}
        </p>
      )}
      {error && (
        <p className="date-field__error" id={errorId} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
